import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Newspaper, LogOut, ShieldCheck,
  ExternalLink, Menu, X, ChevronRight, ShoppingBag, Globe,
} from 'lucide-react';
import { useMyAuth } from '../../context/MyAuthContext';

const ROLE_LABELS = {
  client: { label: 'Client', color: 'text-gray-400 bg-gray-500/10' },
  editeur: { label: 'Éditeur', color: 'text-blue-400 bg-blue-500/10' },
  manager: { label: 'Manager', color: 'text-purple-400 bg-purple-500/10' },
  admin: { label: 'Admin', color: 'text-amber-400 bg-amber-500/10' },
  superadmin: { label: 'Super Admin', color: 'text-[#0099D6] bg-[#0099D6]/10' },
};

function roleLevel(role) {
  return ['client', 'editeur', 'manager', 'admin', 'superadmin'].indexOf(role);
}

export default function MyLayout({ children, title }) {
  const { user, logout } = useMyAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/my', { replace: true });
  };

  const navItems = [
    { to: '/my/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', minRole: 'client' },
    { to: '/my/actualites', icon: Newspaper, label: 'Actualités', minRole: 'editeur' },
    { to: '/my/boutique', icon: ShoppingBag, label: 'Boutique Meaux', minRole: 'editeur' },
    { to: '/my/sites', icon: Globe, label: 'Sites', minRole: 'editeur' },
    { to: '/my/utilisateurs', icon: Users, label: 'Utilisateurs', minRole: 'admin' },
  ].filter(item => roleLevel(user?.role) >= roleLevel(item.minRole));

  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.client;

  return (
    <div className="min-h-screen bg-[#0d0f14] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#12141a] border-r border-white/5
        transform transition-transform duration-200
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <Link to="/my/dashboard" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#0099D6] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-['Outfit'] font-bold text-sm text-white">METERIS</span>
              <span className="block text-[9px] text-gray-500 uppercase tracking-wider">Administration</span>
            </div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded-lg hover:bg-white/5">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Utilisateur */}
        {user && (
          <div className="px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2.5">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#0099D6]/20 flex items-center justify-center text-[#0099D6] text-xs font-bold">
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-white truncate">{user.name || 'Utilisateur'}</p>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${roleInfo.color}`}>
                  {roleInfo.label}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-3 space-y-0.5 flex-1">
          {navItems.map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                pathname === item.to
                  ? 'bg-[#0099D6]/10 text-[#0099D6]'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{item.label}</span>
              {pathname === item.to && <ChevronRight className="w-3 h-3" />}
            </Link>
          ))}
        </nav>

        {/* Bas de sidebar */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/5 space-y-0.5">
          <a
            href="/"
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Voir le site
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors w-full text-left"
          >
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Contenu principal */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0d0f14]/90 backdrop-blur border-b border-white/5 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Menu className="w-5 h-5 text-gray-400" />
              </button>
              <h1 className="font-['Outfit'] text-lg font-semibold text-white">{title}</h1>
            </div>
            <span className="text-xs text-gray-500 hidden sm:block">{user?.email}</span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
